import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  MessageCircle, 
  X, 
  Send, 
  ChevronDown, 
  ChevronRight,
  Users,
  Search,
  Check,
  CheckCheck,
  Loader2,
  Wifi,
  WifiOff,
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneIncoming,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Settings,
  Bell,
  BellOff,
  Circle,
  Clock,
  Coffee,
  MinusCircle
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import FloatingCallWidget from './FloatingCallWidget';
import axios from 'axios';
import { format, isToday, isYesterday } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
// WebSocket URL - use the same backend URL but with ws/wss protocol
const WS_URL = (() => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
})();

// Gentle notification sound - soft ding
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRpQFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YXAFAAB/f39/f39/f4CAgICBgYKCg4OEhIWFhoaHh4iIiYmKiouLjIyNjY6Oj4+QkJGRkpKTk5SUlZWVlZaWl5eXl5iYmJiYmJiYl5eXl5aWlpaVlZSUk5OSkpGRkJCPj46OjYyMi4uKiYmIh4eGhYWEg4OCgYGAgH9/fn59fXx8e3t6enl5eHh3d3Z2dXV0dHNzcnJxcXBwb29ubm1tbGxra2pqaWloaGdnZmZlZWRkY2NiYmFhYGBfX15eXV1cXFtbWlpZWVhYV1dWVlVVVFRTU1JSUVFQUFBQUFBQUFBQUFFRUVFRUlJSU1NUVFVVV1dYWVpbXF1eX2BiY2RlZ2hpa2xtb3BydHV3eXp8fX+AgYOEhoeJiouNjpCRkpSVl5iZm5ydnqChoqOkpaanqKmqq6ytrq6vsLCwsbGxsbGxsLCwr6+urq2sq6qpqKempaOioJ+dnJqZl5aUkpGPjYuKiIaEgoB/fXt5d3VzcXBubGtpZ2VkYmFfXl1cW1pZWFdXVlZVVVVUVFRUVFRVVVVWVldYWVpbXF5fYWJkZWdpam1ub3Fzc3R1dXV1dXV1dXR0c3NycXFwb25tbGtqaWhnZmVkY2JhYF9eXVxbWllYV1ZWVVVUVFNTU1NTU1NTU1RUVFVVVldXWFlaW1xdXl9gYWJjZGVmZ2hpamtsbW5vcHFyc3R1dnd4eXp7fH1+f4CAgYKDhIWGh4iJiouMjY6PkJGSk5SVlpeYmZqbnJ2en6ChoqOkpaanqKmqq6ytrq+wsbKys7O0tLS0tLSzs7KysbCvrq2sq6qpqKempaOioJ+dnJqZl5aUkpCOjIqIhoSCgH58enl3dXNxb21raWdlY2FfXVtZV1VTUlBOTUpIRkRCQD48Ojg2NDIwLiwqKCYkIiAeHBsZFxYUExIQDw4NDAsKCQgHBgUFBAMCAQEBAA==';

// Soft calling/dialing tone - gentle pulse
const CALLING_SOUND = 'data:audio/wav;base64,UklGRoQEAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YWAEAAB/f39/gICAgICAgYGBgYKCgoKDg4ODhISEhIWFhYWGhoaGh4eHh4iIiIiJiYmJioqKiouLi4uMjIyMjY2NjY6Ojo6Pj4+PkJCQkJGRkZGSkpKSk5OTk5SUlJSVlZWVlpaWlpeXl5eYmJiYmZmZmZqampqbm5ubnJycnJ2dnZ2enp6en5+fn6CgoKChoaGhoqKioqOjo6OkpKSkpaWlpaampqanp6enqKioqKmpqamqqqqrrKytra2trq6ur6+vsLCwsbGxsrKys7Ozs7S0tLW1tba2tre3t7i4uLm5ubo=';

// Call connected - short pleasant chime
const CONNECTED_SOUND = 'data:audio/wav;base64,UklGRkQDAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YSADAAB/f4CAgYGCgoODhISFhYaGh4eIiImJioqLi4yMjY2Ojo+PkJCRkZKSk5OUlJWVlpaXl5iYmZmampubm5ycnZ2enp+foKChoaKio6OkpKWlpqanp6ioqamqqqqrrKytra2urq+vsLCxsbKys7O0tLW1tra3t7i4ubm6uru7vLy9vb6+v7/AwMHBwsLDw8TExcXGxsfHyMjJycrKy8vMzM3Nzs7Pz9DQ0dHS0tPT1NTV1dbW19fY2NnZ2tra29vc3N3d3t7f3+Dg4eHi4uPj5OTl5ebm5+fo6Onp6urr6+zs7e3u7u/v8PDx8fLy8/P09PX19vb39/j4+fn6+vv7/Pz9/f7+////';

// WebRTC configuration with STUN/TURN servers

// WebRTC configuration with STUN/TURN servers
const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' }
  ],
  iceCandidatePoolSize: 10
};

const ChatSlideout = () => {
  const { user, token, getAuthHeaders } = useAuth();
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  
  // Sound settings
  const [isSoundMuted, setIsSoundMuted] = useState(false);
  const [mutedUsers, setMutedUsers] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('chat_muted_users');
    return saved ? JSON.parse(saved) : [];
  });
  const [showSoundMenu, setShowSoundMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [myStatus, setMyStatus] = useState('online'); // online, in_meeting, away, offline
  
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callingRef = useRef(null);
  const connectedRef = useRef(null);
  const disconnectRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false); // Prevent duplicate connection attempts
  
  // Voice call state
  const [callState, setCallState] = useState(null); // null | 'calling' | 'incoming' | 'connected'
  const [currentCall, setCurrentCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isCallMinimized, setIsCallMinimized] = useState(false); // For floating widget
  const [audioLevel, setAudioLevel] = useState(0); // Microphone audio level 0-100
  
  // WebRTC refs
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);
  const callingIntervalRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioLevelIntervalRef = useRef(null);
  
  // Refs for state values needed in WebSocket handlers (prevents stale closures)
  const selectedConversationRef = useRef(null);
  const userRef = useRef(null);
  const callStateRef = useRef(null);
  const currentCallRef = useRef(null);
  const isSoundMutedRef = useRef(false);
  const mutedUsersRef = useRef([]);
  
  // Keep refs in sync with state
  useEffect(() => { selectedConversationRef.current = selectedConversation; }, [selectedConversation]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { callStateRef.current = callState; }, [callState]);
  useEffect(() => { currentCallRef.current = currentCall; }, [currentCall]);
  useEffect(() => { isSoundMutedRef.current = isSoundMuted; }, [isSoundMuted]);
  useEffect(() => { mutedUsersRef.current = mutedUsers; }, [mutedUsers]);

  // Initialize audio
  useEffect(() => {
    // Message received notification sound
    audioRef.current = new Audio('/message.ogg');
    audioRef.current.volume = 0.3;
    
    // Incoming call ringtone - what receiver hears
    ringtoneRef.current = new Audio('/ring_incoming.mp3');
    ringtoneRef.current.volume = 0.5;
    ringtoneRef.current.loop = true;
    
    // Outgoing call sound - what caller hears while waiting
    callingRef.current = new Audio('/calling.ogg');
    callingRef.current.volume = 0.3;
    callingRef.current.loop = true;
    
    // Call connected sound
    connectedRef.current = new Audio(CONNECTED_SOUND);
    connectedRef.current.volume = 0.5;
    
    // Call disconnect/hangup sound
    disconnectRef.current = new Audio('/disconnect.ogg');
    disconnectRef.current.volume = 0.4;
  }, []);

  // Save muted users to localStorage when changed
  useEffect(() => {
    localStorage.setItem('chat_muted_users', JSON.stringify(mutedUsers));
  }, [mutedUsers]);

  // Status options configuration
  const statusOptions = [
    { value: 'online', label: 'Online', color: 'bg-green-500', icon: Circle },
    { value: 'in_meeting', label: 'In Meeting', color: 'bg-yellow-500', icon: Clock },
    { value: 'away', label: 'Away', color: 'bg-orange-500', icon: Coffee },
    { value: 'offline', label: 'Appear Offline', color: 'bg-gray-400', icon: MinusCircle }
  ];

  // Get status display info
  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  // Get status dot color for a user
  const getUserStatusColor = (userObj) => {
    if (!userObj?.is_online) return 'bg-gray-400'; // Offline
    const status = userObj?.status || 'online';
    const statusInfo = getStatusInfo(status);
    return statusInfo.color;
  };

  // Get status tooltip for a user
  const getUserStatusTooltip = (userObj) => {
    if (!userObj?.is_online) return 'Offline';
    const status = userObj?.status || 'online';
    const statusInfo = getStatusInfo(status);
    return statusInfo.label;
  };

  // Update user status
  const updateMyStatus = useCallback(async (newStatus) => {
    try {
      await axios.put(`${API_URL}/chat/status`, { status: newStatus }, getAuthHeaders());
      setMyStatus(newStatus);
      
      // Also send via WebSocket for real-time update
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'status_update',
          status: newStatus
        }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
    setShowStatusMenu(false);
  }, [getAuthHeaders]);

  // Fetch current status on mount
  useEffect(() => {
    const fetchMyStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/chat/status`, getAuthHeaders());
        if (res.data?.status) {
          setMyStatus(res.data.status);
        }
      } catch (error) {
        console.error('Failed to fetch status:', error);
      }
    };
    if (token) {
      fetchMyStatus();
    }
  }, [token, getAuthHeaders]);

  // Toggle mute for a specific user
  const toggleMuteUser = useCallback((userId) => {
    setMutedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  }, []);

  // Check if sounds should play for a message
  const shouldPlaySound = useCallback((senderId) => {
    if (isSoundMuted) return false;
    if (mutedUsers.includes(senderId)) return false;
    return true;
  }, [isSoundMuted, mutedUsers]);

  // Play notification sound (with mute check)
  const playNotificationSound = useCallback((senderId) => {
    if (!shouldPlaySound(senderId)) return;
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [shouldPlaySound]);

  // Play/stop ringtone (incoming call)
  const startRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch(() => {});
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  // Play/stop calling sound (outgoing call)
  const startCallingSound = useCallback(() => {
    if (callingRef.current) {
      callingRef.current.currentTime = 0;
      callingRef.current.play().catch(() => {});
    }
  }, []);

  const stopCallingSound = useCallback(() => {
    if (callingRef.current) {
      callingRef.current.pause();
      callingRef.current.currentTime = 0;
    }
  }, []);

  // Play connected sound
  const playConnectedSound = useCallback(() => {
    if (connectedRef.current) {
      connectedRef.current.currentTime = 0;
      connectedRef.current.play().catch(() => {});
    }
  }, []);

  // Start audio level monitoring
  const startAudioLevelMonitoring = useCallback((stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      microphone.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      // Update audio level every 50ms
      audioLevelIntervalRef.current = setInterval(() => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          // Calculate average volume
          const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          // Normalize to 0-100 scale with some amplification
          const normalizedLevel = Math.min(100, (average / 128) * 100 * 1.5);
          setAudioLevel(normalizedLevel);
        }
      }, 50);
    } catch (error) {
      console.warn('Audio level monitoring not available:', error);
    }
  }, []);

  // Stop audio level monitoring
  const stopAudioLevelMonitoring = useCallback(() => {
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  // Cleanup WebRTC connection
  const cleanupCall = useCallback(() => {
    stopRingtone();
    stopCallingSound();
    stopAudioLevelMonitoring();
    
    // Play disconnect sound
    if (disconnectRef.current) {
      disconnectRef.current.currentTime = 0;
      disconnectRef.current.play().catch(() => {});
    }
    
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    pendingCandidatesRef.current = [];
    
    setCallState(null);
    setCurrentCall(null);
    setCallDuration(0);
    setIsMuted(false);
    setIsCallMinimized(false);
  }, [stopRingtone, stopCallingSound, stopAudioLevelMonitoring]);

  // Initialize WebRTC peer connection
  const createPeerConnection = useCallback((targetId) => {
    console.log('Creating peer connection for target:', targetId);
    const pc = new RTCPeerConnection(RTC_CONFIG);
    
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        console.log('Sending ICE candidate to:', targetId);
        wsRef.current.send(JSON.stringify({
          type: 'webrtc_ice_candidate',
          target_id: targetId,
          candidate: event.candidate
        }));
      }
    };
    
    pc.ontrack = (event) => {
      console.log('Received remote track:', event.track.kind);
      if (remoteAudioRef.current && event.streams[0]) {
        remoteAudioRef.current.srcObject = event.streams[0];
        remoteAudioRef.current.play().catch(e => console.log('Audio play error:', e));
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        console.log('WebRTC connection established!');
      }
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
        console.log('WebRTC connection lost');
        cleanupCall();
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
    };
    
    return pc;
  }, [cleanupCall]);

  // Start a voice call
  const initiateCall = useCallback(async (recipientId, recipientName) => {
    if (callState) return; // Already in a call
    
    try {
      // Get user media (audio only)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      
      // Start audio level monitoring
      startAudioLevelMonitoring(stream);
      
      const callId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      setCurrentCall({
        call_id: callId,
        recipient_id: recipientId,
        recipient_name: recipientName,
        is_caller: true
      });
      setCallState('calling');
      startCallingSound(); // Play calling sound
      
      // Create peer connection and add tracks
      const pc = createPeerConnection(recipientId);
      stream.getTracks().forEach(track => {
        console.log('Adding local track:', track.kind);
        pc.addTrack(track, stream);
      });
      peerConnectionRef.current = pc;
      
      // Send call initiation via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        console.log('📞 Sending call_initiate to', recipientId, 'call_id:', callId);
        wsRef.current.send(JSON.stringify({
          type: 'call_initiate',
          recipient_id: recipientId,
          call_id: callId
        }));
      } else {
        console.error('📞 WebSocket not open! Cannot send call_initiate');
      }
      
      // Create and send offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false
      });
      await pc.setLocalDescription(offer);
      console.log('Created offer, sending to recipient');
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'webrtc_offer',
          recipient_id: recipientId,
          call_id: callId,
          offer: offer
        }));
      }
    } catch (error) {
      console.error('Failed to initiate call:', error);
      cleanupCall();
      alert('Could not start call. Please ensure microphone access is allowed.');
    }
  }, [callState, createPeerConnection, cleanupCall, startCallingSound]);

  // Handle incoming call
  const handleIncomingCall = useCallback((data) => {
    console.log('Incoming call:', data);
    if (callState) {
      // Already in a call, auto-decline
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'call_decline',
          call_id: data.call_id,
          caller_id: data.caller_id
        }));
      }
      return;
    }
    
    setCurrentCall({
      call_id: data.call_id,
      caller_id: data.caller_id,
      caller_name: data.caller_name,
      caller_avatar: data.caller_avatar,
      is_caller: false
    });
    setCallState('incoming');
    startRingtone(); // Play ringtone
  }, [callState, startRingtone]);

  // Accept incoming call
  const acceptCall = useCallback(async () => {
    if (!currentCall || callState !== 'incoming') return;
    
    stopRingtone();
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      
      // Start audio level monitoring
      startAudioLevelMonitoring(stream);
      
      // Create peer connection
      const pc = createPeerConnection(currentCall.caller_id);
      stream.getTracks().forEach(track => {
        console.log('Adding local track (recipient):', track.kind);
        pc.addTrack(track, stream);
      });
      peerConnectionRef.current = pc;
      
      // If we have a pending offer, process it now
      if (currentCall.pendingOffer) {
        console.log('Processing pending offer');
        await pc.setRemoteDescription(new RTCSessionDescription(currentCall.pendingOffer.offer));
        
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'webrtc_answer',
            caller_id: currentCall.pendingOffer.caller_id,
            call_id: currentCall.pendingOffer.call_id,
            answer: answer
          }));
        }
      }
      
      // Notify caller that call was accepted
      console.log('📞 Sending call_accept to caller', currentCall.caller_id, 'call_id:', currentCall.call_id);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'call_accept',
          call_id: currentCall.call_id,
          caller_id: currentCall.caller_id
        }));
        console.log('📞 call_accept sent successfully');
      } else {
        console.error('📞 WebSocket not open! Cannot send call_accept');
      }
      
      // Process any pending ICE candidates
      for (const candidate of pendingCandidatesRef.current) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.error('Error adding pending ICE candidate:', e);
        }
      }
      pendingCandidatesRef.current = [];
      
      setCallState('connected');
      playConnectedSound(); // Play connected sound
      
      // Start call duration timer
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to accept call:', error);
      declineCall();
    }
  }, [currentCall, callState, stopRingtone, createPeerConnection, playConnectedSound]);

  // Decline incoming call
  const declineCall = useCallback(() => {
    if (!currentCall) return;
    
    stopRingtone();
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'call_decline',
        call_id: currentCall.call_id,
        caller_id: currentCall.caller_id
      }));
    }
    
    cleanupCall();
  }, [currentCall, stopRingtone, cleanupCall]);

  // End current call
  const endCall = useCallback(() => {
    if (!currentCall) return;
    
    const otherUserId = currentCall.is_caller ? currentCall.recipient_id : currentCall.caller_id;
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: currentCall.is_caller && callState === 'calling' ? 'call_cancel' : 'call_end',
        call_id: currentCall.call_id,
        other_user_id: otherUserId,
        recipient_id: otherUserId // For cancel
      }));
    }
    
    cleanupCall();
  }, [currentCall, callState, cleanupCall]);

  // Handle call accepted by recipient
  const handleCallAccepted = useCallback(async (data) => {
    console.log('Call accepted:', data);
    stopCallingSound(); // Stop calling sound
    playConnectedSound(); // Play connected sound
    setCallState('connected');
    
    // Start call duration timer
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, [stopCallingSound, playConnectedSound]);

  // Handle call declined
  const handleCallDeclined = useCallback((data) => {
    console.log('Call declined:', data);
    stopCallingSound();
    cleanupCall();
  }, [cleanupCall, stopCallingSound]);

  // Handle call ended by other party
  const handleCallEnded = useCallback((data) => {
    console.log('Call ended:', data);
    cleanupCall();
  }, [cleanupCall]);

  // Handle call cancelled
  const handleCallCancelled = useCallback((data) => {
    console.log('Call cancelled:', data);
    stopRingtone();
    cleanupCall();
  }, [stopRingtone, cleanupCall]);

  // Handle WebRTC offer (for recipient)
  const handleWebRTCOffer = useCallback(async (data) => {
    console.log('Received WebRTC offer from:', data.caller_id);
    
    // If we don't have a peer connection yet, we need to wait for user to accept
    // Store the offer and process it when the call is accepted
    if (!peerConnectionRef.current) {
      console.log('No peer connection yet, storing offer for later');
      // Store offer data to process after accepting
      if (currentCall) {
        setCurrentCall(prev => ({ ...prev, pendingOffer: data }));
      }
      return;
    }
    
    try {
      console.log('Setting remote description from offer');
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.offer));
      
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);
      console.log('Created answer, sending to caller');
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'webrtc_answer',
          caller_id: data.caller_id,
          call_id: data.call_id,
          answer: answer
        }));
      }
      
      // Process any pending ICE candidates
      for (const candidate of pendingCandidatesRef.current) {
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.error('Error adding pending ICE candidate:', e);
        }
      }
      pendingCandidatesRef.current = [];
    } catch (error) {
      console.error('Failed to handle WebRTC offer:', error);
    }
  }, [currentCall]);

  // Handle WebRTC answer (for caller)
  const handleWebRTCAnswer = useCallback(async (data) => {
    console.log('Received WebRTC answer');
    if (peerConnectionRef.current) {
      try {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
        console.log('Remote description set successfully');
      } catch (error) {
        console.error('Failed to set remote description:', error);
      }
    }
  }, []);

  // Handle ICE candidate
  const handleICECandidate = useCallback(async (data) => {
    console.log('Received ICE candidate');
    if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
      try {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
        console.log('ICE candidate added');
      } catch (error) {
        console.error('Failed to add ICE candidate:', error);
      }
    } else {
      // Store candidate to add later
      console.log('Storing ICE candidate for later');
      pendingCandidatesRef.current.push(data.candidate);
    }
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch unread count (fallback for initial load)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/chat/unread-count`, getAuthHeaders());
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [getAuthHeaders]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/chat/conversations`, getAuthHeaders());
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, [getAuthHeaders]);

  // Fetch available users
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/chat/users`, getAuthHeaders());
      console.log('📋 Fetched users:', res.data.map(u => ({ name: u.name, is_online: u.is_online })));
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [getAuthHeaders]);

  // Fetch messages for selected conversation
  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const res = await axios.get(
        `${API_URL}/chat/conversations/${conversationId}/messages`,
        getAuthHeaders()
      );
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [getAuthHeaders]);

  // Store fetchConversations in a ref for use in WebSocket handler
  const fetchConversationsRef = useRef(fetchConversations);
  useEffect(() => { fetchConversationsRef.current = fetchConversations; }, [fetchConversations]);

  // Store callback functions in refs to avoid stale closures and prevent reconnection loops
  const cleanupCallRef = useRef(cleanupCall);
  useEffect(() => { cleanupCallRef.current = cleanupCall; }, [cleanupCall]);
  
  const handleWebRTCOfferRef = useRef(handleWebRTCOffer);
  useEffect(() => { handleWebRTCOfferRef.current = handleWebRTCOffer; }, [handleWebRTCOffer]);
  
  const handleWebRTCAnswerRef = useRef(handleWebRTCAnswer);
  useEffect(() => { handleWebRTCAnswerRef.current = handleWebRTCAnswer; }, [handleWebRTCAnswer]);
  
  const handleICECandidateRef = useRef(handleICECandidate);
  useEffect(() => { handleICECandidateRef.current = handleICECandidate; }, [handleICECandidate]);

  // =============================================
  // STABLE WebSocket Connection Management
  // Uses refs to avoid stale closures and prevent reconnection loops
  // Only connect when chat slideout is actually open to prevent unnecessary connections
  // =============================================
  useEffect(() => {
    // Only connect WebSocket when chat is open AND we have a token
    if (!token || !isOpen) {
      // Cleanup existing connection when chat is closed
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        wsRef.current.close(1000, 'Chat closed');
        wsRef.current = null;
      }
      setIsConnected(false);
      return;
    }
    
    // Prevent duplicate connection attempts
    if (isConnectingRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    isConnectingRef.current = true;
    const wsUrl = `${WS_URL}/api/ws/chat/${token}`;
    console.log('Connecting to WebSocket...');
    
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket creation failed:', error);
      isConnectingRef.current = false;
      return;
    }

    ws.onopen = () => {
      console.log('WebSocket connected');
      isConnectingRef.current = false;
      setIsConnected(true);
      
      // Clear any pending reconnect
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Start ping interval - frequent pings ensure timely delivery of cross-instance messages
      // This is especially important for call signaling in multi-server deployments
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      pingIntervalRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
        }
      }, 5000); // 5 second ping interval for better cross-instance message delivery
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const currentConv = selectedConversationRef.current;
        const currentUser = userRef.current;
        const currentCallState = callStateRef.current;
        
        // Log all incoming WebSocket messages for debugging
        if (data.type !== 'pong') {
          console.log('WebSocket message received:', data.type, data);
        }
        
        switch (data.type) {
          case 'new_message': {
            const message = data.data;
            // Add message to current conversation if it matches
            if (currentConv?.id === message.conversation_id) {
              setMessages(prev => {
                if (prev.some(m => m.id === message.id)) return prev;
                return [...prev, message];
              });
              
              // Mark as read if from someone else
              if (message.sender_id !== currentUser?.id && wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                  type: 'read',
                  conversation_id: message.conversation_id
                }));
              }
            }
            
            // Play sound for messages from others (with mute check)
            if (message.sender_id !== currentUser?.id) {
              const soundMuted = isSoundMutedRef.current;
              const userMuted = mutedUsersRef.current.includes(message.sender_id);
              
              if (!soundMuted && !userMuted && audioRef.current) {
                audioRef.current.currentTime = 0;
                audioRef.current.play().catch(() => {});
              }
              setUnreadCount(prev => prev + 1);
            }
            
            // Update conversation list
            fetchConversationsRef.current?.();
            break;
          }
          
          case 'typing': {
            const typingData = data.data;
            setTypingUsers(prev => {
              if (typingData.is_typing) {
                return { ...prev, [typingData.conversation_id]: typingData.user_name };
              } else {
                const newState = { ...prev };
                delete newState[typingData.conversation_id];
                return newState;
              }
            });
            
            // Auto-clear typing indicator after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => {
                const newState = { ...prev };
                delete newState[typingData.conversation_id];
                return newState;
              });
            }, 3000);
            break;
          }
          
          case 'read_receipt': {
            const receiptData = data.data;
            if (currentConv?.id === receiptData.conversation_id) {
              setMessages(prev => prev.map(msg => 
                msg.sender_id === currentUser?.id ? { ...msg, is_read: true } : msg
              ));
            }
            break;
          }
          
          case 'pong':
          case 'connected':
            if (data.type === 'connected') console.log('WebSocket authenticated');
            break;
            
          case 'incoming_call': {
            const callData = data.data;
            console.log('Incoming call received:', callData);
            if (currentCallState) {
              // Already in a call, auto-decline
              console.log('Already in a call, declining');
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                  type: 'call_decline',
                  call_id: callData.call_id,
                  caller_id: callData.caller_id
                }));
              }
              return;
            }
            
            setCurrentCall({
              call_id: callData.call_id,
              caller_id: callData.caller_id,
              caller_name: callData.caller_name,
              caller_avatar: callData.caller_avatar,
              is_caller: false
            });
            setCallState('incoming');
            
            // Play ringtone using ref to avoid stale closure
            if (ringtoneRef.current) {
              ringtoneRef.current.currentTime = 0;
              ringtoneRef.current.play().catch(err => console.log('Ringtone play error:', err));
            }
            break;
          }
          
          case 'call_accepted': {
            console.log('📞 CALL ACCEPTED received!', data);
            if (callingRef.current) {
              callingRef.current.pause();
              callingRef.current.currentTime = 0;
              console.log('📞 Stopped calling sound');
            }
            if (connectedRef.current) {
              connectedRef.current.currentTime = 0;
              connectedRef.current.play().catch(() => {});
              console.log('📞 Playing connected sound');
            }
            setCallState('connected');
            console.log('📞 Call state set to connected');
            
            // Start call duration timer
            if (callTimerRef.current) clearInterval(callTimerRef.current);
            callTimerRef.current = setInterval(() => {
              setCallDuration(prev => prev + 1);
            }, 1000);
            break;
          }
          
          case 'call_declined':
            console.log('📞 CALL DECLINED received!', data);
            if (callingRef.current) {
              callingRef.current.pause();
              callingRef.current.currentTime = 0;
            }
            cleanupCallRef.current();
            break;
            
          case 'call_ended':
            console.log('📞 CALL ENDED received!', data);
            cleanupCallRef.current();
            break;
            
          case 'call_cancelled':
            console.log('📞 CALL CANCELLED received!', data);
            stopRingtone();
            cleanupCallRef.current();
            break;
            
          case 'webrtc_offer':
            handleWebRTCOfferRef.current(data.data);
            break;
            
          case 'webrtc_answer':
            handleWebRTCAnswerRef.current(data.data);
            break;
            
          case 'webrtc_ice_candidate':
            handleICECandidateRef.current(data.data);
            break;
          
          case 'user_status': {
            // Update user online status in real-time
            const statusData = data.data;
            console.log('📡 Received user_status update:', statusData);
            setUsers(prevUsers => prevUsers.map(u => 
              u.id === statusData.user_id 
                ? { ...u, is_online: statusData.is_online, status: statusData.status || u.status }
                : u
            ));
            // Also update conversations participant status
            setConversations(prevConvs => prevConvs.map(conv => ({
              ...conv,
              participants: conv.participants?.map(p => 
                p.id === statusData.user_id 
                  ? { ...p, is_online: statusData.is_online, status: statusData.status || p.status }
                  : p
              )
            })));
            break;
          }
            
          default:
            break;
        }
      } catch (e) {
        console.error('WebSocket parse error:', e);
      }
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      isConnectingRef.current = false;
      setIsConnected(false);
      wsRef.current = null;
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      // Only reconnect on abnormal close, not on intentional close (1000) or no status (1005)
      // Also check if we still have a token (user is still logged in)
      if (event.code !== 1000 && event.code !== 1005) {
        reconnectTimeoutRef.current = setTimeout(() => {
          // Re-trigger connection by changing nothing - the effect will re-run due to token
          // Actually we need to manually retry since token doesn't change
          if (wsRef.current === null && !isConnectingRef.current) {
            isConnectingRef.current = false; // Allow reconnection attempt
          }
        }, 5000);
      }
    };

    ws.onerror = (error) => {
      console.log('WebSocket error:', error);
      isConnectingRef.current = false;
    };

    // Cleanup function
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        ws.close(1000, 'Component unmount');
      }
      wsRef.current = null;
      isConnectingRef.current = false;
    };
  }, [token, isOpen]); // Depend on token AND isOpen - connect only when chat is open

  // Initial data fetch
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Load data when opening
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([fetchConversations(), fetchUsers()]).finally(() => {
        setLoading(false);
      });
    }
  }, [isOpen, fetchConversations, fetchUsers]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation?.id) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: selectedConversation.id,
        is_typing: isTyping
      }));
    }
  }, [selectedConversation?.id]);

  // Handle message input change with typing indicator
  const handleMessageChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    sendTypingIndicator(true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing indicator after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 2000);
  };

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;

    // Stop typing indicator
    sendTypingIndicator(false);

    setSending(true);
    try {
      const payload = {
        content: newMessage.trim(),
        conversation_id: selectedConversation?.id || null,
        recipient_id: selectedConversation?.recipientId || null
      };

      const res = await axios.post(`${API_URL}/chat/messages`, payload, getAuthHeaders());
      
      // If this was a new conversation, update selected conversation
      if (!selectedConversation?.id && res.data.conversation_id) {
        setSelectedConversation({
          ...selectedConversation,
          id: res.data.conversation_id
        });
      }
      
      // Message will be added via WebSocket, but add immediately for responsiveness
      setMessages(prev => {
        if (prev.some(m => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  // Start conversation with user
  const startConversation = (chatUser) => {
    const existing = conversations.find(c => 
      c.participants.length === 2 && 
      c.participants.some(p => p.id === chatUser.id)
    );

    if (existing) {
      selectConversation(existing);
    } else {
      setSelectedConversation({
        id: null,
        recipientId: chatUser.id,
        participants: [
          { id: user?.id, name: user?.name, role: user?.role },
          { id: chatUser.id, name: chatUser.name, role: chatUser.role }
        ]
      });
      setMessages([]);
    }
    setActiveTab('conversations');
  };

  // Select existing conversation
  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setLoading(true);
    await fetchMessages(conv.id);
    setLoading(false);
    
    // Mark as read via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'read',
        conversation_id: conv.id
      }));
    }
    
    // Also via REST API as fallback
    try {
      await axios.put(`${API_URL}/chat/conversations/${conv.id}/read`, {}, getAuthHeaders());
      fetchUnreadCount();
      fetchConversations();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Get other participant name
  const getOtherParticipant = (conv) => {
    const other = conv.participants?.find(p => p.id !== user?.id);
    return other?.name || 'Unknown';
  };

  // Format message time
  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) return format(date, 'h:mm a');
    if (isYesterday(date)) return 'Yesterday ' + format(date, 'h:mm a');
    return format(date, 'MMM d, h:mm a');
  };

  // Group users by organization
  const groupedUsers = users.reduce((acc, u) => {
    const org = u.organization_name || 'Platform';
    if (!acc[org]) acc[org] = [];
    acc[org].push(u);
    return acc;
  }, {});

  // Filter users by search
  const filteredGroupedUsers = Object.entries(groupedUsers).reduce((acc, [org, orgUsers]) => {
    const filtered = orgUsers.filter(u => 
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) acc[org] = filtered;
    return acc;
  }, {});

  const toggleOrg = (org) => {
    setExpandedOrgs(prev => ({ ...prev, [org]: !prev[org] }));
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-105"
        style={{
          background: isDark 
            ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'
            : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
        }}
        data-testid="chat-toggle-btn"
      >
        <MessageCircle size={24} className="text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => {
            setIsOpen(false);
            setSelectedConversation(null);
          }}
        />
      )}

      {/* Click outside to close sound menu */}
      {showSoundMenu && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setShowSoundMenu(false)}
        />
      )}

      {/* Click outside to close status menu */}
      {showStatusMenu && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setShowStatusMenu(false)}
        />
      )}

      {/* Slideout Panel */}
      <div 
        className={`fixed top-0 right-0 h-full z-50 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: '380px' }}
        data-testid="chat-slideout-panel"
      >
        <div 
          className="h-full flex flex-col shadow-2xl"
          style={{
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderLeft: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`
          }}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{
              borderColor: isDark ? '#334155' : '#e2e8f0',
              backgroundColor: isDark ? '#0f172a' : '#f8fafc'
            }}
          >
            <div className="flex items-center gap-2">
              <MessageCircle size={20} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
              <h2 className={`font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                Team Chat
              </h2>
              {/* Connection indicator */}
              <span className={`flex items-center gap-1 text-xs ${isConnected ? 'text-green-500' : 'text-yellow-500'}`}>
                {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Status Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                  }`}
                  title="Set your status"
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${getStatusInfo(myStatus).color}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    {getStatusInfo(myStatus).label}
                  </span>
                  <ChevronDown size={12} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
                </button>
                
                {/* Status Dropdown Menu */}
                {showStatusMenu && (
                  <div 
                    className={`absolute left-0 top-full mt-1 w-44 rounded-lg shadow-lg border z-50 ${
                      isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
                    }`}
                  >
                    <div className="p-1">
                      {statusOptions.map((option) => {
                        const Icon = option.icon;
                        return (
                          <button
                            key={option.value}
                            onClick={() => updateMyStatus(option.value)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                              myStatus === option.value
                                ? isDark ? 'bg-slate-700' : 'bg-slate-100'
                                : isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-50'
                            }`}
                          >
                            <span className={`w-2.5 h-2.5 rounded-full ${option.color}`} />
                            <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                              {option.label}
                            </span>
                            {myStatus === option.value && (
                              <Check size={14} className="ml-auto text-green-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Sound Settings Button */}
              <div className="relative">
                <button
                  onClick={() => setShowSoundMenu(!showSoundMenu)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isSoundMuted 
                      ? 'text-red-400 hover:bg-red-500/20' 
                      : isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                  }`}
                  title="Sound settings"
                >
                  {isSoundMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                
                {/* Sound Menu Dropdown */}
                {showSoundMenu && (
                  <div 
                    className={`absolute right-0 top-full mt-1 w-64 rounded-lg shadow-lg border z-50 ${
                      isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
                    }`}
                  >
                    <div className="p-2">
                      {/* Mute All */}
                      <button
                        onClick={() => {
                          setIsSoundMuted(!isSoundMuted);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                          isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                        }`}
                      >
                        {isSoundMuted ? <VolumeX size={16} className="text-red-400" /> : <Volume2 size={16} />}
                        <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                          {isSoundMuted ? 'Unmute All Sounds' : 'Mute All Sounds'}
                        </span>
                      </button>
                      
                      {/* Divider */}
                      <div className={`my-2 border-t ${isDark ? 'border-slate-600' : 'border-slate-200'}`} />
                      
                      {/* Per-user mute */}
                      <div className={`px-3 py-1 text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Mute Individual Users
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {users.map(u => (
                          <button
                            key={u.id}
                            onClick={() => toggleMuteUser(u.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                              isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                            }`}
                          >
                            {mutedUsers.includes(u.id) 
                              ? <BellOff size={14} className="text-red-400" /> 
                              : <Bell size={14} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
                            }
                            <span className={`flex-1 text-left truncate ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                              {u.name}
                            </span>
                            {/* Online status dot */}
                            <span 
                              className={`w-2 h-2 rounded-full ${getUserStatusColor(u)}`}
                              title={getUserStatusTooltip(u)}
                            />
                            {mutedUsers.includes(u.id) && (
                              <span className="text-xs text-red-400">Muted</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Close Button */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSelectedConversation(null);
                  setShowSoundMenu(false);
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          {!selectedConversation && (
            <div className="flex border-b" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
              <button
                onClick={() => setActiveTab('conversations')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === 'conversations'
                    ? isDark 
                      ? 'text-blue-400 border-b-2 border-blue-400' 
                      : 'text-blue-600 border-b-2 border-blue-600'
                    : isDark 
                      ? 'text-slate-400 hover:text-slate-200' 
                      : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Conversations
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === 'users'
                    ? isDark 
                      ? 'text-blue-400 border-b-2 border-blue-400' 
                      : 'text-blue-600 border-b-2 border-blue-600'
                    : isDark 
                      ? 'text-slate-400 hover:text-slate-200' 
                      : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Users size={14} className="inline mr-1" />
                New Chat
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading && !selectedConversation ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className={`w-6 h-6 animate-spin ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
              </div>
            ) : selectedConversation ? (
              /* Message View */
              <div className="flex flex-col h-full">
                {/* Conversation Header */}
                <div 
                  className="flex items-center gap-3 px-4 py-3 border-b"
                  style={{
                    borderColor: isDark ? '#334155' : '#e2e8f0',
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(248, 250, 252, 0.5)'
                  }}
                >
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className={`p-1 rounded ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                  >
                    <ChevronRight size={20} className={`transform rotate-180 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                  </button>
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {getOtherParticipant(selectedConversation)?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium text-sm ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                        {getOtherParticipant(selectedConversation)}
                      </p>
                      {/* Voice call button */}
                      {(() => {
                        const otherParticipant = selectedConversation.participants?.find(p => p.id !== user?.id);
                        const isOnline = otherParticipant?.is_online;
                        return null; // Call feature temporarily hidden
                        /* CALL FEATURE HIDDEN - Uncomment to re-enable
                        return (
                          <button
                            onClick={() => {
                              if (otherParticipant) {
                                if (!isOnline) {
                                  alert(`${otherParticipant.name} is currently offline. They won't receive the call notification until they come online.`);
                                }
                                initiateCall(otherParticipant.id, otherParticipant.name);
                              }
                            }}
                            disabled={callState !== null}
                            className={`flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full transition-colors ${
                              callState 
                                ? 'opacity-50 cursor-not-allowed bg-slate-200 text-slate-500' 
                                : isOnline
                                  ? isDark 
                                    ? 'bg-green-600 hover:bg-green-500 text-white' 
                                    : 'bg-green-500 hover:bg-green-600 text-white'
                                  : isDark
                                    ? 'bg-yellow-600 hover:bg-yellow-500 text-white'
                                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                            }`}
                            data-testid="voice-call-btn"
                            title={isOnline ? 'Start voice call' : 'User is offline - call may not be received'}
                          >
                            <Phone size={12} />
                            {isOnline ? 'Call' : 'Offline'}
                          </button>
                        );
                        */
                      })()}
                    </div>
                    {/* Typing indicator */}
                    {typingUsers[selectedConversation.id] && (
                      <p className="text-xs text-blue-500 animate-pulse">
                        typing...
                      </p>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Start the conversation</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] px-3 py-2 rounded-2xl ${
                            msg.sender_id === user?.id
                              ? 'bg-blue-600 text-white rounded-br-md'
                              : isDark
                                ? 'bg-slate-700 text-slate-100 rounded-bl-md'
                                : 'bg-slate-100 text-slate-900 rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                          <div className={`flex items-center justify-end gap-1 mt-1 ${
                            msg.sender_id === user?.id ? 'text-blue-200' : isDark ? 'text-slate-500' : 'text-slate-400'
                          }`}>
                            <span className="text-xs">{formatMessageTime(msg.created_at)}</span>
                            {msg.sender_id === user?.id && (
                              msg.is_read 
                                ? <CheckCheck size={12} className="text-blue-300" /> 
                                : <Check size={12} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form 
                  onSubmit={handleSendMessage}
                  className="p-3 border-t"
                  style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}
                >
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={handleMessageChange}
                      placeholder="Type a message..."
                      className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600 text-slate-100' : ''}`}
                      disabled={sending}
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="px-3"
                    >
                      {sending ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            ) : activeTab === 'conversations' ? (
              /* Conversations List */
              <div className="divide-y" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
                {conversations.length === 0 ? (
                  <div className={`text-center py-12 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    <MessageCircle size={40} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs mt-1">Start chatting with your team!</p>
                  </div>
                ) : (
                  conversations.map((conv) => {
                    const otherUser = conv.participants?.find(p => p.id !== user?.id);
                    return (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                        isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Avatar with status dot */}
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {getOtherParticipant(conv)?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        {/* Online status dot */}
                        <span 
                          className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                            isDark ? 'border-slate-800' : 'border-white'
                          } ${getUserStatusColor(otherUser)}`}
                          title={getUserStatusTooltip(otherUser)}
                        />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center justify-between">
                          <p className={`font-medium text-sm truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                            {getOtherParticipant(conv)}
                          </p>
                          {conv.last_message_at && (
                            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                              {formatMessageTime(conv.last_message_at)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {typingUsers[conv.id] ? (
                              <span className="text-blue-500 animate-pulse">typing...</span>
                            ) : (
                              conv.last_message || 'No messages yet'
                            )}
                          </p>
                          {conv.unread_count > 0 && (
                            <span className="w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  )})
                )}
              </div>
            ) : (
              /* Users List */
              <div>
                {/* Search */}
                <div className="p-3">
                  <div className="relative">
                    <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className={`pl-9 ${isDark ? 'bg-slate-700 border-slate-600 text-slate-100' : ''}`}
                    />
                  </div>
                </div>

                {/* Grouped Users */}
                <div className="divide-y" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
                  {Object.entries(filteredGroupedUsers).map(([org, orgUsers]) => (
                    <div key={org}>
                      <button
                        onClick={() => toggleOrg(org)}
                        className={`w-full px-4 py-2.5 flex items-center justify-between ${
                          isDark ? 'bg-slate-800/50 hover:bg-slate-700/50' : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <span className={`text-xs font-semibold uppercase tracking-wide ${
                          isDark ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                          {org}
                        </span>
                        {expandedOrgs[org] ? (
                          <ChevronDown size={16} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                        ) : (
                          <ChevronRight size={16} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                        )}
                      </button>
                      {expandedOrgs[org] !== false && (
                        <div>
                          {orgUsers.map((chatUser) => (
                            <button
                              key={chatUser.id}
                              onClick={() => startConversation(chatUser)}
                              className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors ${
                                isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
                              }`}
                            >
                              {/* Avatar with status dot */}
                              <div className="relative flex-shrink-0">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                                  <span className="text-white text-xs font-medium">
                                    {chatUser.name?.charAt(0)?.toUpperCase()}
                                  </span>
                                </div>
                                {/* Online status dot */}
                                <span 
                                  className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${
                                    isDark ? 'border-slate-800' : 'border-white'
                                  } ${getUserStatusColor(chatUser)}`}
                                  title={getUserStatusTooltip(chatUser)}
                                />
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                                  {chatUser.name}
                                </p>
                                <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                  {chatUser.role === 'super_admin' ? 'Platform Admin' : chatUser.role}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Remote Audio Element (hidden) */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />

      {/* CALL FEATURE HIDDEN - Incoming Call Modal and FloatingCallWidget commented out
      {callState === 'incoming' && currentCall && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          data-testid="incoming-call-modal"
        >
          <div 
            className={`rounded-2xl p-8 shadow-2xl animate-pulse-slow max-w-sm w-full mx-4 ${
              isDark ? 'bg-slate-800' : 'bg-white'
            }`}
          >
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4 animate-bounce">
                <PhoneIncoming size={36} className="text-white" />
              </div>
              <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Incoming Call
              </h3>
              <p className={`text-lg mb-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {currentCall.caller_name}
              </p>
              <div className="flex justify-center gap-6">
                <button
                  onClick={declineCall}
                  className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
                  data-testid="decline-call-btn"
                >
                  <PhoneOff size={24} className="text-white" />
                </button>
                <button
                  onClick={acceptCall}
                  className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors shadow-lg animate-pulse"
                  data-testid="accept-call-btn"
                >
                  <Phone size={24} className="text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FloatingCallWidget
        callState={callState}
        currentCall={currentCall}
        callDuration={callDuration}
        isMuted={isMuted}
        onToggleMute={toggleMute}
        onEndCall={endCall}
        onMinimize={() => setIsCallMinimized(!isCallMinimized)}
        isMinimized={isCallMinimized}
        audioLevel={audioLevel}
      />
      */}
    </>
  );
};

export default ChatSlideout;
